import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import { useRef, useState } from "react";
import { ReactComponent as ExpandIcon } from "../../assets/images/expand-icon.svg";
import {
  chipKey,
  EMPTY_VALUE_DASH,
  gridSize3Column as gridSize,
  labelKey,
  viewMoreKey,
  viewMorePopOverGridSize,
} from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { isNotEmpty } from "../../helpers/utils";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { activityTimelineSiteCoreKeys } from "../../pages/createProgram/siteCore/SiteCoreKeys";
import { MuiBox } from "../box/MuiBox";
import MuiChipWithPopover from "../chipWithPopover/MuiChipWithPopover";
import { DateTimeChip } from "../dateTimeChip/DateTimeChip";
import { MuiDoubleLabel } from "../doubleLabel/MuiDoubleLabel";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiGrid } from "../grid/MuiGrid";
import { MuiLink } from "../link/MuiLink";
import { SearchSaveFiltersSubHeader } from "../searchSaveFiltersSubHeader/SearchSaveFiltersSubHeader";
import { OptionsDataType } from "../select/MuiSelect";
import { MuiViewMorePopOver } from "../tooltip/viewMorePopOver/ViewMorePopOver";
import { MuiTypography } from "../typography/MuiTypography";
import {
  TimelineActivityDivider,
  TimelineActivityHeaderWrapper,
  TimelineCollapsableActivityWrapper,
} from "./TimelineActivity.style";

export type ViewType = "label" | "chip" | "viewMore";

export interface TimelineActivityDataItem {
  type: ViewType;
  label: string;
  value: string | Array<{ label: string; value: string }>;
  replaceEmptyWithDash?: boolean;
  viewMoreAnchorText?: string;
}

export interface TimelineActivityType {
  id: string;
  timeStamp: string;
  fromToTimeDuration?: string;
  isTodaySelected?: boolean;
  title: string;
  displayWith?: boolean;
  creator: string;
  instructor?: string;
  isCollapsible: boolean;
  showMoreOptions?: boolean;
  expandableData: TimelineActivityDataItem[];
  handleEdit?: (id: string) => void;
  handleDelete?: (id: string) => void;
  isNotClosureType?: boolean;
  startDateTime?: string;
  endDateTime?: string;
  category?: string;
  hasDeletePermission?: boolean;
}

const TimelineActivity = ({
  id,
  timeStamp,
  title,
  displayWith,
  creator,
  expandableData,
  isCollapsible,
  showMoreOptions,
  handleEdit,
  handleDelete,
  isNotClosureType = true,
  isTodaySelected,
  hasDeletePermission = true,
}: TimelineActivityType) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const handleChange = () => {
    setIsExpanded(!isExpanded);
  };

  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const renderLabelType = (item: TimelineActivityDataItem) => (
    <MuiDoubleLabel
      label={item.label}
      replaceEmptyWithDash={item?.replaceEmptyWithDash ?? true}
      isValueWithEllipsis
      value={item.value as string}
    />
  );

  const renderViewMoreType = (item: TimelineActivityDataItem) => (
    <MuiDoubleLabel
      label={item.label}
      replaceEmptyWithDash={item?.replaceEmptyWithDash ?? true}
      value={
        isNotEmpty(item.value) ? (
          <MuiViewMorePopOver
            anchorText={commonLabels.viewMore}
            message={item.value as string}
          />
        ) : (
          ""
        )
      }
    />
  );

  const renderChipType = (item: TimelineActivityDataItem) => (
    <>
      <MuiTypography component={"h3"} variant={"h3"}>
        {item.label}
      </MuiTypography>
      {!isEmpty(item.value) ? (
        <MuiChipWithPopover
          data={item.value as OptionsDataType[]}
          handleDelete={() => {}}
          isEditable={false}
          headerLabel={item.label}
          gridRef={gridRef}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ) : (
        <MuiTypography
          component={"h4"}
          className="value-style"
          variant={"h4Bold"}
        >
          {EMPTY_VALUE_DASH}
        </MuiTypography>
      )}
    </>
  );

  const renderItemByType = (item: TimelineActivityDataItem) => {
    if (item.type === labelKey) {
      return renderLabelType(item);
    }
    if (item.type === viewMoreKey) {
      return renderViewMoreType(item);
    }
    if (item.type === chipKey) {
      return renderChipType(item);
    }
    return null;
  };

  const renderAccordionDetails = (data: TimelineActivityDataItem[]) => {
    return data.map((item) => (
      <MuiGrid
        size={item.type === viewMoreKey ? viewMorePopOverGridSize : gridSize}
        ref={gridRef}
        key={`${item.label}-${item.type}`}
      >
        {renderItemByType(item)}
      </MuiGrid>
    ));
  };

  const renderSearchButtonContainer = () => {
    let actions = [
      ...(hasDeletePermission
        ? [
            {
              id: "delete",
              label: commonLabels.delete,
              ariaLabel: commonLabels.delete,
              icon: <HighlightOffIcon />,
              onClick: () => handleDelete?.(id),
              showOutsideMobile: true,
              testId: `${COMPONENT_IDS.COMMON_OPTION_MENU}-action`,
            },
          ]
        : []),
    ];
    actions.push({
      id: "edit",
      label: commonLabels.edit,
      ariaLabel: commonLabels.edit,
      icon: <EditOutlinedIcon />,
      onClick: () => handleEdit?.(id),
      showOutsideMobile: false,
      testId: `${COMPONENT_IDS.COMMON_OPTION_MENU}-action`,
    });
    return <SearchSaveFiltersSubHeader additionalActions={actions} />;
  };

  return (
    <TimelineCollapsableActivityWrapper>
      <Accordion expanded={isExpanded} onChange={handleChange}>
        <AccordionSummary
          expandIcon={
            isCollapsible && (
              <ExpandIcon data-testid="expand-icon" fontSize="large" />
            )
          }
        >
          <TimelineActivityHeaderWrapper>
            <DateTimeChip
              isTodaySelected={isTodaySelected}
              timestamp={timeStamp}
            />
            <TimelineActivityDivider />
            {isCollapsible ? (
              <MuiLink role="none" component={"label"}>
                <MuiBox>
                  <MuiEllipseLabel
                    data-testid="activity-title-link"
                    labelTypoClassName="Link activity-title-link"
                    text={title}
                    labelTypoVariant="h3Bold"
                    placement="bottom-start"
                    pointerCursor
                  />
                </MuiBox>
              </MuiLink>
            ) : (
              <MuiTypography variant="h3Bold" className="label">
                {title}
              </MuiTypography>
            )}
            {isNotClosureType && (
              <MuiTypography className="body3">
                {displayWith
                  ? commonLabels.with
                  : fetchLabel(
                      activityTimelineSiteCoreKeys.activityTimeline,
                      activityTimelineSiteCoreKeys.byLabel,
                    )}{" "}
                {creator}
              </MuiTypography>
            )}
          </TimelineActivityHeaderWrapper>
        </AccordionSummary>
        {isCollapsible && (
          <AccordionDetails>
            {showMoreOptions && renderSearchButtonContainer()}
            <MuiGrid container className="accordion-details">
              {renderAccordionDetails(expandableData)}
            </MuiGrid>
          </AccordionDetails>
        )}
      </Accordion>
    </TimelineCollapsableActivityWrapper>
  );
};

export { TimelineActivity };
